import deIndexJson from '@/data/contentIndex.de.json'
import enIndexJson from '@/data/contentIndex.en.json'
import type { ContentIndex, RelatedRef, Technique } from '@/lib/types'
import { CATEGORIES } from '@/lib/categories'
import { getLang, type Lang } from '@/i18n'

export const contentIndexDe = deIndexJson as ContentIndex
export const contentIndexEn = enIndexJson as ContentIndex

/** @deprecated use contentIndexFor(lang) — defaults to active language */
export const contentIndex = contentIndexDe

export function contentIndexFor(lang?: Lang): ContentIndex {
  const l = lang || getLang()
  return l === 'en' ? contentIndexEn : contentIndexDe
}

export function getAllTechniques(lang?: Lang): Technique[] {
  return contentIndexFor(lang).techniques
}

export function getTechnique(slug: string, lang?: Lang): Technique | undefined {
  return contentIndexFor(lang).techniques.find((t) => t.slug === slug)
}

export function getTechniquesByCategory(categoryId: string, lang?: Lang): Technique[] {
  return contentIndexFor(lang).techniques.filter((t) => t.categoryId === categoryId)
}

export function relatedSlug(ref: RelatedRef): string {
  return typeof ref === 'string' ? ref : ref.slug
}

export function getRelated(tech: Technique, lang?: Lang): Technique[] {
  return (tech.related || [])
    .map((s) => getTechnique(relatedSlug(s), lang))
    .filter((t): t is Technique => Boolean(t))
}

export function getChains(lang?: Lang) {
  return contentIndexFor(lang).chains
}

export function getChain(slug: string, lang?: Lang) {
  return contentIndexFor(lang).chains.find((c) => c.slug === slug)
}

export function getCategoryMeta(id: string, lang?: Lang) {
  return (
    contentIndexFor(lang).categories.find((c) => c.id === id) ||
    CATEGORIES.find((c) => c.id === id) || { id, label: id, count: 0, order: 99 }
  )
}

export function techniqueUrl(t: Technique, lang?: Lang): string {
  const l = lang || getLang()
  return `/${l}/${t.categoryId}/${t.slug}`
}

/** Resolve explainatory image path for active language; null → show placeholder. */
export function resolveTechniqueImage(tech: Technique, lang?: Lang): string | null {
  const l = lang || getLang()
  if (l === 'en') {
    if (tech.hasEnImage) return `explainatories/en/${tech.slug}-en.png`
    if (tech.image && /\/en\//.test(tech.image)) return tech.image.replace(/^\/+/, '')
    return null
  }
  if (tech.hasDeImage || (tech.image && /\/de\//.test(tech.image))) {
    return (tech.image || `explainatories/de/${tech.slug}-de.png`).replace(/^\/+/, '')
  }
  if (tech.image) return tech.image.replace(/^\/+/, '')
  return null
}
